import {useRef, useState} from "react";
import {KatBox, KatButton, KatInput, KatModal, KatSpinner} from "@amzn/katal-react";
import * as React from "react";
import {LighthouseApi} from "src/app/api/LighthouseApi";
import {BulkSubmitDecisionResponse} from "src/app/api/model/response/BulkSubmitDecisionResponse";

interface BulkActionDialogProps {
    visible: boolean;
    selectedIds: Set<string>;
    onClose: () => void;
    onSuccess: () => void;
}

type DialogState = 'input' | 'loading' | 'success' | 'partial' | 'error';

const BulkActionDialog = (props: BulkActionDialogProps) => {
    const {visible, selectedIds, onClose, onSuccess} = props;
    const notesRef = useRef<any>(null);
    const [state, setState] = useState<DialogState>('input');
    const [result, setResult] = useState<BulkSubmitDecisionResponse | null>(null);
    const [errorMsg, setErrorMsg] = useState('');

    const [notesError, setNotesError] = useState(false);

    const resetAndClose = () => {
        // If we've already completed (success/partial), always refresh on close
        if (state === 'success' || state === 'partial') {
            handleDismissAndRefresh();
            return;
        }
        setState('input');
        setResult(null);
        setErrorMsg('');
        setNotesError(false);
        if (notesRef.current) {
            notesRef.current.value = '';
        }
        onClose();
    };

    const handleConfirm = async () => {
        const notes = notesRef.current?.value?.trim();
        if (!notes) {
            setNotesError(true);
            notesRef.current?.focus();
            return;
        }
        setNotesError(false);

        setState('loading');
        try {
            const response = await LighthouseApi.bulkSubmitDecision({
                reviewIds: Array.from(selectedIds),
                notes
            });
            setResult(response);
            setState(response.totalFailed === 0 ? 'success' : 'partial');
        } catch (error: any) {
            setErrorMsg(error.message || 'Unexpected error occurred');
            setState('error');
        }
    };

    const handleDismissAndRefresh = () => {
        setState('input');
        setResult(null);
        setErrorMsg('');
        if (notesRef.current) {
            notesRef.current.value = '';
        }
        onSuccess();
    };

    const renderInput = () => (
        <>
            <span slot="title">Bulk No-Action</span>
            <KatBox>
                <p><b>{selectedIds.size} case(s)</b> selected for No-Action.</p>
                <KatInput
                    label="Reason (mandatory)"
                    ref={notesRef}
                    required={true}
                    maxlength={4000}
                    state={notesError ? 'error' : undefined}
                    stateLabel={notesError ? 'Please provide a reason' : undefined}
                    placeholder="e.g. Erroneous Cradle job output - SIM-98765"
                    data-testid="bulk-notes-input"
                    onInput={() => notesError && setNotesError(false)}
                />
            </KatBox>
            <div slot="footer" className="kat-group-horizontal">
                <KatButton variant="secondary" label="Cancel"
                           onClick={resetAndClose} data-testid="bulk-cancel-btn"/>
                <KatButton label="Confirm" onClick={handleConfirm} data-testid="bulk-confirm-btn"/>
            </div>
        </>
    );

    const renderLoading = () => (
        <>
            <span slot="title">Processing...</span>
            <KatBox>
                <div style={{textAlign: 'center', padding: '20px'}}>
                    <KatSpinner size="large"/>
                    <p>Submitting {selectedIds.size} case(s) for No-Action...</p>
                </div>
            </KatBox>
        </>
    );

    const renderSuccess = () => (
        <>
            <span slot="title">Bulk No-Action Complete</span>
            <KatBox>
                <p>{result?.totalProcessed} case(s) closed successfully.</p>
            </KatBox>
            <div slot="footer" className="kat-group-horizontal">
                <KatButton label="Dismiss & Refresh" onClick={handleDismissAndRefresh}
                           data-testid="bulk-dismiss-btn"/>
            </div>
        </>
    );

    const renderPartial = () => (
        <>
            <span slot="title">Bulk No-Action — Partial Result</span>
            <KatBox>
                <p>
                    <b>{result?.totalProcessed}</b> closed
                    &nbsp;|&nbsp;
                    <b>{result?.totalFailed}</b> failed
                </p>
                {result?.failedReviewIds && result.failedReviewIds.length > 0 && (
                    <div style={{marginTop: '10px'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <b>Failed Cases:</b>
                            <KatButton variant="secondary" size="small"
                                       onClick={() => navigator.clipboard.writeText(
                                           result.failedReviewIds!.join('\n')
                                       )}
                                       data-testid="copy-failed-btn">
                                Copy IDs
                            </KatButton>
                        </div>
                        <ul style={{maxHeight: '200px', overflowY: 'auto', margin: '8px 0'}}>
                            {result.failedReviewIds.map(id => (
                                <li key={id}>{id}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </KatBox>
            <div slot="footer" className="kat-group-horizontal">
                <KatButton label="Dismiss & Refresh" onClick={handleDismissAndRefresh}
                           data-testid="bulk-dismiss-btn"/>
            </div>
        </>
    );

    const renderError = () => (
        <>
            <span slot="title">Bulk No-Action Failed</span>
            <KatBox>
                <p style={{color: '#d13212'}}>{errorMsg}</p>
            </KatBox>
            <div slot="footer" className="kat-group-horizontal">
                <KatButton variant="secondary" label="Close" onClick={resetAndClose}
                           data-testid="bulk-error-close-btn"/>
            </div>
        </>
    );

    const handleModalClose = () => {
        // Block closing while API call is in flight
        if (state === 'loading') return;
        resetAndClose();
    };

    return (
        <KatModal visible={visible} data-testid="bulk-action-modal" onClose={handleModalClose}>
            {state === 'input' && renderInput()}
            {state === 'loading' && renderLoading()}
            {state === 'success' && renderSuccess()}
            {state === 'partial' && renderPartial()}
            {state === 'error' && renderError()}
        </KatModal>
    );
};

export default BulkActionDialog;
